import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../lib/auth/auth-context'
import { safeReturnTo } from '../lib/auth/return-to'

/**
 * Auth gate for the vertical routes (UID-9). Wrap a group of vertical routes
 * as a layout route (`<Route element={<RequireAuth />}>`) so every screen the
 * IMTS verticals (WS-2..5) mount inherits the same rule: an unauthenticated
 * visitor is redirected to the login screen, not shown a vertical screen.
 *
 * Three states, matching the auth context (F0.2):
 *  - `loading`: the initial `GET /auth/session` probe is still in flight —
 *    render nothing (a flash of the login screen only to swap to content a
 *    moment later reads as broken; same rationale as the shell's nav).
 *  - `unauthenticated` (or the account-less `onboarding_required` pre-session,
 *    which cannot enter a vertical): redirect to `/login`, forwarding the
 *    attempted path as a same-origin-validated `returnTo` so login can send
 *    the user back where they were headed.
 *  - `authenticated`: render the nested vertical route.
 *
 * This is UX routing only — the BFF independently authorizes every API call a
 * vertical makes (a 401 mid-session is caught globally by `authMiddleware` and
 * flips the store to unauthenticated, which re-renders this gate).
 */
export function RequireAuth() {
  const auth = useAuth()
  const location = useLocation()

  if (auth.status === 'loading') {
    return null
  }

  if (auth.status !== 'authenticated') {
    const returnTo = safeReturnTo(`${location.pathname}${location.search}`, '/')
    const to = returnTo === '/' ? '/login' : `/login?returnTo=${encodeURIComponent(returnTo)}`
    return <Navigate to={to} replace />
  }

  return <Outlet />
}
